import React from 'react'
import Link from 'next/link'
import { GuildShield } from '@/atoms/Icons/GuildShield'
import { AsDiv, CaptionMD, EyebrowLG, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { NamedColor } from '@/constants/types'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Avatar } from '@/molecules/Avatar'
import { Membership, useFetchUserMembership } from '@/services/MembershipService/MembershipService'
import {
  hasAccess,
  isAccessInvestor,
  isSubProvisional,
  isSubPurchaseBlocked,
} from '@/services/MembershipService/membership-status'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { logger } from '@/utils/logging'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { getUserFirstName, getUserLastName } from '@/utils/users'
import { formatShortPlanName } from '@/views/GuildGiftView/tiers'

interface Props {
  handleItemClick: React.MouseEventHandler<HTMLAnchorElement>
}

export const UserMenuHeader: React.FC<Props> = ({ handleItemClick }) => {
  const { t } = useTranslate('common')
  const { locale } = useLocale()
  const { user } = useUser()
  const { data, dataState } = useFetchUserMembership()
  const { isDarkMode } = useThemeContext()
  const membership: Membership | null | undefined = dataState === 'complete' ? data.user?.myMembership : undefined
  const membershipStatusEyebrowConfig: MembershipStatusEyebrowConfig = mapMembershipStatusEyebrowConfig(
    t,
    membership,
    isDarkMode,
  )

  const firstName = getUserFirstName(user)
  const lastName = getUserLastName(user)
  let name = ''
  if (firstName && lastName) {
    name = `${firstName} ${lastName.charAt(0)}.`
  } else if (firstName) {
    name = firstName
  }

  const sinceDate = membership?.memberSince
    ? new Date(membership.memberSince).toLocaleDateString(locale, { dateStyle: 'short' })
    : undefined
  return (
    <div className="-mx-4 mb-3 flex gap-3 border-y border-core-gray-900 px-4 py-5 md:border-t-0">
      <div className="shrink-0">
        <Avatar size={48} />
      </div>
      <div className="flex w-full flex-wrap justify-between gap-x-3 gap-y-2">
        <div>
          <TitleMD weight="semibold" color={isDarkMode ? 'white' : 'core-gray-950'}>
            {name !== '' ? name : t('anonymous', 'Anonymous')}
          </TitleMD>
          {!!user?.email && <CaptionMD color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>{user.email}</CaptionMD>}
        </div>

        {dataState === 'complete' && (
          <div>
            <div className="flex items-center gap-1">
              {hasAccess(membership) && <GuildShield size={12} />}
              <EyebrowLG weight="semibold" color={membershipStatusEyebrowConfig.color} className="uppercase">
                {membershipStatusEyebrowConfig.name}
              </EyebrowLG>
            </div>
            {hasAccess(membership) && !!membership?.memberSince && (
              <div data-testid="memberSince">
                <CaptionMD as={AsDiv} color={isDarkMode ? 'core-gray-500' : 'core-gray-600'}>
                  <Translate t={t} i18nKey="sinceDate" values={{ sinceDate }}>
                    Since <span>{{ sinceDate }}</span>
                  </Translate>
                </CaptionMD>
              </div>
            )}
            {!hasAccess(membership) && !isSubPurchaseBlocked(membership) && (
              <Link href={paths.guild.join} onClick={handleItemClick}>
                <CaptionMD color="core-oxide-bright">{t('unlockBenefits', 'Unlock Benefits')}</CaptionMD>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

interface MembershipStatusEyebrowConfig {
  name: string
  color: NamedColor
}

function mapMembershipStatusEyebrowConfig(
  t: TranslateFunction,
  membership: Membership | undefined | null,
  isDarkMode: boolean,
): MembershipStatusEyebrowConfig {
  if (isSubProvisional(membership?.primaryProduct?.subscription))
    return { name: t('processingMembership', 'Processing…'), color: 'core-copper' as NamedColor }

  if (isAccessInvestor(membership))
    return { name: t('investor', 'Investor'), color: isDarkMode ? 'white' : ('core-gray-950' as NamedColor) }

  if (hasAccess(membership)) return mapActiveGuildMemberConfig(t, membership)

  return {
    name: t('freeAccount', 'Free Account'),
    color: isDarkMode ? 'core-gray-400' : ('core-gray-700' as NamedColor),
  }

  function mapActiveGuildMemberConfig(t: TranslateFunction, membership: Membership | undefined | null) {
    let planName = formatShortPlanName(t, membership?.primaryProduct?.tier)
    if (!planName) {
      logger().error('Missing plan name for membership', { membership })
      planName = t('membership', 'Membership')
    }
    return {
      name: planName,
      color: 'core-copper' as NamedColor,
    }
  }
}
